import React, { FC, useCallback } from 'react';
import * as actions from '../mfaTokensActions';
import { Field } from '../types';
import UserIdentifierDropdown from './UserIdentifierDropdown';
import TokenTextField from './TokenTextField';
import { PING_MFA_TOKEN_FIELDS } from '../../../common/fields';
import TokenActions from './TokenActions';
import { TokenComponentProps } from './index';

interface UpdateAction {
    type: typeof actions.UPDATE_PING_MFA_TOKEN;
    payload: {
        fieldId: string;
        value: string;
    };
}

type DispatchFn = (action: UpdateAction) => void;

export interface PingFields {
    pingUpnField: Field;
    pingApiKey: Field;
    pingToken: Field;
    pingOrganizationAlias: Field;
    pingIdpUrl: Field;
}

export type PingProps = TokenComponentProps<PingFields, DispatchFn>;

const Ping: FC<PingProps> = ({ fields, dispatch, onSave, onCancel }) => {
    const pingUpnField = fields[PING_MFA_TOKEN_FIELDS.PING_UPN_FIELD.ID];
    const pingApiKey = fields[PING_MFA_TOKEN_FIELDS.PING_API_KEY.ID];
    const pingToken = fields[PING_MFA_TOKEN_FIELDS.PING_TOKEN.ID];
    const pingOrganizationAlias = fields[PING_MFA_TOKEN_FIELDS.PING_ORGANIZATION_ALIAS.ID];
    const pingIdpUrl = fields[PING_MFA_TOKEN_FIELDS.PING_IDP_URL.ID];

    const isSomeFieldsEmpty = [pingApiKey, pingToken, pingOrganizationAlias, pingIdpUrl].some((field) => !field.value);

    const textFields = [
        { field: pingApiKey, isSecret: true, placeholder: 'Enter API key' },
        { field: pingToken, isSecret: true, placeholder: 'Enter token' },
        { field: pingOrganizationAlias, isSecret: false, placeholder: 'Enter alias' },
        { field: pingIdpUrl, isSecret: false, placeholder: 'Enter URL' }
    ];

    const dispatchUpdate = useCallback(
        (fieldId: string, value: string) => {
            dispatch({
                type: actions.UPDATE_PING_MFA_TOKEN,
                payload: { fieldId, value }
            });
        },
        [dispatch]
    );

    const pingUpnOptions = (pingUpnField.options || []).map((option) => ({
        title: option.label,
        value: option.value
    }));

    return (
        <>
            <UserIdentifierDropdown
                options={pingUpnOptions}
                selectedOption={pingUpnField.value}
                onChange={(val) => dispatchUpdate(pingUpnField.id, val)}
                mandatory
                label={pingUpnField.name}
            />
            {textFields.map(({ field, isSecret, placeholder }) => (
                <TokenTextField
                    key={field.id}
                    label={field.name}
                    value={field.value}
                    isSecret={isSecret}
                    placeholder={placeholder}
                    onChange={(val) => dispatchUpdate(field.id, val)}
                />
            ))}

            <TokenActions onSave={onSave} onCancel={onCancel} saveDisabled={isSomeFieldsEmpty} />
        </>
    );
};

export default Ping;
